/** @module csi/comm/Aspera */
define([
	"dojo/Deferred",
	"dojo/promise/all",
	"underscore",
	"csi/access/crews",
	"csi/comm/PostRequest",
	"csi/locale",
	"csi/message",
	"csi/sessionConstants",
	"csi/xml",
], function(Deferred,all,_,crews,PostRequest,locale,message,sessionConstants,xml) {

// ----- CONSTANTS ----
	const STR_ASPERA  = "Aspera";
	const ASPERA_LOCATION = "/WebAccess/Aspera/v4";
	const URL_ASPERA_WEB = "/WebAccess/Aspera/v4/asperaweb-4.js";

	const EVENT_TRANSFER = "transfer";
	const OP_DOWNLOAD = "Download";
	const OP_UPLOAD = "Upload";
	const ERR_LIC_EXPIRED = 30;
	const ERR_CONNECTION_ERROR_HTTP = 47;
	let isAsperaValid = false;

//TODO: delete later or use for local plugin storage
//	const location = window.location;
//	const nodeInterpreter = location.protocol + "//" + location.host + "/csi-requesthandler/AsperaNodeConnect";

// ----- COMMON FUNCTIONS ----
	/**
	 * Show Progress message
	 * @param {string} msg
	 * @param {Controller} ac - action controller
	 */
	function showInfo(msg, ac){
		displayProgressDialog(msg, ac);
		message.showInfo(msg);
		console.info(consoleMessage(msg));

	}
	/**
	 * Show Progress message
	 * @param {string} msg
	 * @param {Controller} ac - action controller
	 * @param {number} [progressValue]
	 */
	function showProgress(msg, ac, progressValue){
		displayProgressDialog(msg, ac, progressValue);
		message.progress(msg);
		console.debug(consoleMessage(msg));
	}

	/**
	 * Show Success message
	 * @param {string} msg
	 * @param {Controller} ac - action controller
	 */
	function showSuccess(msg, ac){
		displayProgressDialog(msg, ac);
		message.success(msg);
		console.debug(consoleMessage(msg));
	}

	/**
	 * Show Error message
	 * @param {string} msg
	 * @param {Controller} ac - action controller
	 */
	function showError(msg, ac){
		displayProgressDialog(msg, ac);
		message.error(msg);
		console.error(consoleMessage(msg));
	}

	/**
	 * Format console message
	 * @param {string} msg
	 * @return {string}
	 */
	function consoleMessage(msg){
		return STR_ASPERA + ": " + msg;
	}

	/**
	 * Display controller's progress dialog with message and propgress value
	 * @param {string} msg
	 * @param {Controller} ac - action controller
	 * @param {number} [progressValue]
	 */
	function displayProgressDialog(msg, ac, progressValue){
		if (!ac){
			return;
		}
		var params = {closable: false};
		if (msg) {
			params.message = msg;
		}
		ac.displayProgressDialog(params);
		if (progressValue){
			ac.displayProgressValue(progressValue);
		}
	}

	/**
	 * Add element "script" to the document head, load the script, resolve deferred
	 * @param {string} url
	 * @return {Deferred}
	 */
	function loadScript(url){
		var dfd = new Deferred();
		var element = document.createElement("script");
		element.setAttribute("type","text/javascript");
		element.setAttribute("src", url);
		element.onload = function(){
			dfd.resolve();
		};
		document.head.appendChild(element);
		return dfd;
	}

	/**
	 * Remove path, validate file name
	 * @param {string} url
	 * @return {string}
	 */
	function fileNameFromUrl(url){
		var filenameRegEx = /[<>:"\/\\|?*\x00-\x1F]/g;
		var fileName = url.substring(url.lastIndexOf('/')+1);
		fileName = fileName.substring(fileName.lastIndexOf('\\')+1);
		var validFileName = !filenameRegEx.test(fileName);
		return validFileName ? fileName :  "fileName";
	}

	/**
	 * Handle transfer events, show progress messages
	 * @param {Deferred} dfd
	 * @param {Controller} ac - action controller
	 * @param {String} event - Aspera event name
	 * @param {AsperaData} returnObj - Aspera event data
	 *   @typedef {Object} AsperaData
	 *   @property {string} asperaSessionId
	 *   @property {Object} asperaWeb - IBM Aspera internal structure
	 *   @property {Object} transferSpec - IBM Aspera internal structure
	 *   @property {Object} connectSettings - IBM Aspera structure, we use to enable/disalbe Aspera monitor window
	 *   @property {string[]} fileNames
	 *   @property {Object[]) files - File blob like object, but without blob and includes full file path
	 * @param {function} thisFunction - the instance of this function
	 * @param {Object} asperaWeb
	 * @param {string} asperaOp - "Upload" or "Download", only used to form right message string
	 * @param {function} callback - callback to be called on success
	 */
	function handleTransferEvents(dfd, ac, event, returnObj, thisFunction, asperaWeb, asperaOp, callback) {
		var obj = returnObj.transfers[0];
		if(!obj){
			obj = {};
			showInfo(".");
		}
		if (obj.status == 'initiating'){
			showProgress(obj.status);
		}
		if (obj.status == 'cancelled'){
			asperaWeb.removeEventListener(EVENT_TRANSFER, thisFunction);
			var msg = locale.resource(STR_ASPERA + asperaOp +"Cancelled");
			showError(msg, ac);
			dfd.reject(msg);
		} else if (obj.status == 'removed'){
			asperaWeb.removeEventListener(EVENT_TRANSFER, thisFunction);
			var msg = locale.resource(STR_ASPERA + asperaOp +"Removed");
			showError(msg, ac);
			dfd.reject(msg);
		} else if (obj.status == 'failed'){
			asperaWeb.removeEventListener(EVENT_TRANSFER, thisFunction);
			var msg = locale.resource(STR_ASPERA + asperaOp +"Failed") + ': ' + obj.error_desc + ' (' + obj.error_code + ')';
			if (obj.error_code == ERR_LIC_EXPIRED || obj.error_code == ERR_CONNECTION_ERROR_HTTP) {
				isAsperaValid = true;
			}
			showError(msg, ac);
			dfd.reject(msg);
		} else if (obj.status == 'completed') {
			asperaWeb.removeEventListener(EVENT_TRANSFER, thisFunction);
			var msg = locale.resource(STR_ASPERA + asperaOp +"Completed");
			if (callback){
				showProgress(msg, ac); // If callback is defined, Success message will be shown there, show Progress here
				callback();
			}else {
				showSuccess(msg, ac); // otherwise just show Success here
				dfd.resolve();
			}
		} else {
			if ((event == EVENT_TRANSFER) && (obj.percentage != null)) {
				const progressValue = obj.percentage;
				showProgress(locale.resource(STR_ASPERA + asperaOp +"ing") + " (" + Math.floor(progressValue * 100) + "%)", ac, progressValue);
			}
		}
	};

// ----- COMMON FUNCTIONS END ----
// ----- File Select ----

	/**
	 * Select file, resolve dfd with asperaDetails
	 * @param {Deferred} dfd
	 * @param {Number} asperaSessionId - if of current Aspera session
	 * @param {Object} asperaWeb
	 * @param {Object} responseData - data received from NodeAPIConnect request
	 * @param {CNL} documentContainerUrl - CNL of fileContainer for uploaded file(s) or CNL of CurrentRevision for file replace operation
	 * @param {Object} params - allowed file extensions
	 */
	function handleSelect(dfd, asperaSessionId, asperaWeb, responseData, documentContainerUrl, params){

		var fileNames = [];
		connectSettings = {
			"allow_dialogs": "no"
		};

		//Start Upload using Connect
		var transferSpec = responseData.transfer_specs[0].transfer_spec;
		transferSpec.authentication = "token";

		asperaWeb.showSelectFileDialog({
			success: function (pathArray) {
				var fileArray = pathArray.dataTransfer.files;
				if (!fileArray.length){
					dfd.reject(locale.resource("ErrorNoFile"));
					return;
				}
				var destination_file;
				for (var i = 0; i < fileArray.length; i++)
				{
					 destination_file = fileArray[i].name;
					 var goodName = fileNameFromUrl(destination_file);
					 destination_file = asperaSessionId +"_"+goodName;//destination_file.substring(index);

					 fileNames.push(goodName);
					 transferSpec.paths[i] = {source: fileArray[i].name , destination: destination_file };
				}
				dfd.resolve({
					asperaSessionId: asperaSessionId,
					asperaWeb: asperaWeb,
					transferSpec: transferSpec,
					connectSettings: connectSettings,
					fileNames: fileNames,
					files: pathArray.dataTransfer.files
				});
			}}, params.options
		);


	}
// ----- File Select END ----
// ----- DOWNLOAD ----

	/**
	 * Process file download, show Aspera dialog, handle Events
	 * @param {Deferred} dfd
	 * @param {Number} asperaSessionId - if of current Aspera session
	 * @param {Object} asperaWeb
	 * @param {Object} responseData - data received from NodeAPIConnect request
	 */
	function handleDownload(dfd, asperaSessionId, asperaWeb, responseData){
		const onTransfer = (event, returnObj) => {
		    handleTransferEvents(dfd, null, event, returnObj, onTransfer, asperaWeb, OP_DOWNLOAD, null);
		}
		asperaWeb.addEventListener(EVENT_TRANSFER, onTransfer);

		// Allow Connect Dialog to appear for downloads
		connectSettings = {
			"allow_dialogs": "yes"
		};

		//Start Download using Connect
		var transferSpec = responseData.transfer_specs[0].transfer_spec;
		//Add token authentication tag to JSON since is it not returned with transferSpec.
		transferSpec.authentication = "token";

		showProgress(locale.resource("AsperaDownloadStarting"));
		asperaWeb.startTransfer(transferSpec, connectSettings);
	}

// ----- DOWNLOAD END ----
// ----- UPLOAD 3D ----

	/**
	 * Aspera upload file to the root of File Vault. After upload file has to be attached to File Container and moved to the correct File VAult folder
	 * @param {Deferred} dfd
	 * @param {string[]} fileNames - several might might being uploaded simultaneously
	 * @param {CNL} documentContainerUrl - CNL of fileContainer for uploaded file(s) or CNL of CurrentRevision for file replace operation
	 * @param {Number} asperaSessionId - id of current Aspera session
	 * @param {string} doc3DName - user defined document name
	 * @param {boolean} bMaterial - create material rather then document
	 * @param {boolean} bPropagate - Propagate3DMaterials
	 */
	function updateFileVault3D(dfd, fileNames, documentContainerUrl , asperaSessionId, doc3DName, bMaterial, bPropagate){
		var req = new PostRequest("SiteAdmin", bMaterial ? "Create3DLargeMaterial" : "Create3DLargeDocument")
			.addParameter("ParentURL", documentContainerUrl)
			.addParameter("DocumentName", doc3DName)
			.addParameter("FileName", fileNames[0])
			.addParameter("Id", asperaSessionId);
		if (bMaterial){
			req.addParameter("Propagate3DMaterials", bPropagate);
		}

		req.post({}).then(
			function (response) {
				showSuccess(locale.resource("AsperaUploadSuccessful"));
				dfd.resolve(response);
			},
			function (err) {
				var msg = err.message;
				showError(msg);
				dfd.reject(msg);
			});
	}

	/**
	 * File is pre-selected, perform upload, handle Events
	 * @param {Deferred} dfd
	 * @param {CNL} documentContainerUrl - CNL of file container
	 * @param {string} docName - user defined document name
	 * @param {HstDataTransfer} asperaData - information about Aspera connection from selectFile()
	 * @param {boolean} bMaterial - create material rather then document
	 * @param {boolean} bPropagate - Propagate3DMaterials
	 * @param {Controller} ac - action controller
	 */
	function handleUpload3D(dfd, documentContainerUrl, docName, asperaData, ac, bMaterial, bPropagate){
		let {asperaWeb, asperaSessionId, transferSpec, connectSettings, fileNames} = asperaData;
		const onSuccess = () => {
		    updateFileVault3D(dfd, fileNames, documentContainerUrl, asperaSessionId, docName, bMaterial, bPropagate);
		};
		const onTransfer = (event, returnObj) => {
		    handleTransferEvents(dfd, ac, event, returnObj, onTransfer, asperaWeb, OP_UPLOAD, onSuccess);
		}
		asperaWeb.addEventListener(EVENT_TRANSFER, onTransfer);

		showProgress(locale.resource("AsperaUploadStarting"), ac);
		asperaWeb.startTransfer(transferSpec, connectSettings);
	}
// ----- UPLOAD 3D END ----

// ----- UPLOAD CONTRACTUAL ----

	/**
	 * Aspera upload file to the root of File Vault. After upload file has to be attached to File Container and moved to the correct File VAult folder
	 * @param {Deferred} dfd
	 * @param {string[]} fileNames - several might might being uploaded simultaneously
	 * @param {CNL} documentContainerUrl - CNL of fileContainer for uploaded file(s) or CNL of CurrentRevision for file replace operation
	 * @param {Number} asperaSessionId - id of current Aspera session
	 * @param {string} documentName - document name
	 */
	function updateFileVaultContractual(dfd, fileNames, documentContainerUrl , asperaSessionId, documentName){
		const fileName = fileNames[0];
		var req = new PostRequest("Quality", "CreateLargeContractualDocument")
			.addParameter("URL", documentContainerUrl)
			.addParameter("FileName", fileName)
			.addParameter("Id", asperaSessionId)
			.addParameter("DocumentName", documentName || fileName);

		req.post({}).then(
			function (response) {
				showSuccess(locale.resource("AsperaUploadSuccessful"));
				dfd.resolve(response);
			},
			function (err) {
				var msg = err.message;
				showError(msg);
				dfd.reject(msg);
			});
	}

	/**
	 * File is pre-selected, perform upload, handle Events
	 * @param {Deferred} dfd
	 * @param {CNL} documentContainerUrl - CNL of file container
	 * @param {HstDataTransfer} asperaData - information about Aspera connection from selectFile()
	 * @param {Controller} ac - action controller
	 */
	function handleUploadContractual(dfd, documentContainerUrl, asperaData, ac){
		if (!asperaData) {
			dfd.reject(locale.resource("ErrorNoFile"));
			return;
		}
		let {asperaWeb, asperaSessionId, transferSpec, connectSettings, fileNames} = asperaData;
		const onSuccess = () => {
			const documentName = ac.getFormData()?.getField(xml.ATTR_NAME)?.getValue?.();
		    updateFileVaultContractual(dfd, fileNames, documentContainerUrl, asperaSessionId, documentName);
		};
		const onTransfer = (event, returnObj) => {
		    handleTransferEvents(dfd, ac, event, returnObj, onTransfer, asperaWeb, OP_UPLOAD, onSuccess);
		}
		asperaWeb.addEventListener(EVENT_TRANSFER, onTransfer);

		showProgress(locale.resource("AsperaUploadStarting"), ac);
		asperaWeb.startTransfer(transferSpec, connectSettings);
	}
// ----- UPLOAD CONTRACTUAL END ----

// ----- UPLOAD  ----
	/**
	 * Aspera uploaded file to the root of File Vault.
	 * This function will attach uploaded file to File Container and move it to correct File Vault folder
	 * @param {Deferred} dfd
	 * @param {string[]} fileNames - several files might be uploaded simultaneously
	 * @param {CNL} documentContainerUrl - CNL of fileContainer for uploaded file(s) or CNL of CurrentRevision for file replace operation
	 * @param {Number} asperaSessionId - id of current Aspera session
	 * @param {boolean} bReplace - replace existing file rather then creating new file
	 */
	function updateFileVault(dfd, fileNames, documentContainerUrl, fileAttr, asperaSessionId, bReplace, isDocument){
		var docType = isDocument ? "Document" : "Image";
		var len = fileNames.length;
		let newNodeAttrId;
		if (isDocument) {
			newNodeAttrId = bReplace ? "" : "Documents";
		}
		if (!fileAttr || fileAttr === "") {
			fileAttr = "File";
		}
		docType += (";" + docType).repeat(len - 1);
		var req = new PostRequest("SiteAdmin", "LargeMultiFilePublish")
			.addParameter("URL", documentContainerUrl)
			.addParameter("FileNames", fileNames)
			.addParameter("NewNodeTypes", bReplace ? "" : docType)
			.addParameter("NewNodeAttrId", newNodeAttrId)
			.addParameter("NewFileAttrId", bReplace ? fileAttr : "")
			.addParameter("Prepend", isDocument ? "false" : "true")
			.addParameter("Id", asperaSessionId);

		req.post({}).then(
			function (response) {
				showSuccess(locale.resource("AsperaUploadSuccessful"));
				dfd.resolve(response);
			},
			function (err) {
				var msg = err.message;
				showError(msg);
				dfd.reject(msg);
			});
	}

	/**
	 * Select file, perform upload, handle Events
	 * @param {Deferred} dfd
	 * @param {Number} asperaSessionId - if of current Aspera session
	 * @param {Object} asperaWeb
	 * @param {Object} responseData - data received from NodeAPIConnect request
	 * @param {CNL} documentContainerUrl - CNL of fileContainer for uploaded file(s) or CNL of CurrentRevision for file replace operation
	 * @param {Object} params
	 */
	function handleUpload(dfd, asperaSessionId, asperaWeb, responseData, documentContainerUrl, fileAttr, isDocument, params){
		var fileNames = [];
		let {ac, bReplace} = params;
		const onSuccess = () => {
		    updateFileVault(dfd, fileNames, documentContainerUrl, fileAttr, asperaSessionId, bReplace, isDocument);
		};
		const onTransfer = (event, returnObj) => {
		    handleTransferEvents(dfd, ac, event, returnObj, onTransfer, asperaWeb, OP_UPLOAD, onSuccess);
		}
		asperaWeb.addEventListener(EVENT_TRANSFER, onTransfer);

		//Block Connect Dialog from appearing, since we are showing progress on web app
		connectSettings = {
			"allow_dialogs": "no"
		};

		//Start Upload using Connect
		var transferSpec = responseData.transfer_specs[0].transfer_spec;
		transferSpec.authentication = "token";

		asperaWeb.showSelectFileDialog({success: function (pathArray) {
			var fileArray = pathArray.dataTransfer.files;
			if (!fileArray.length){
				asperaWeb.removeEventListener(EVENT_TRANSFER, onTransfer);
				dfd.reject(locale.resource("ErrorNoFile"));
				return;
			}
			var destination_file;
			for (var i = 0; i < fileArray.length; i++)
			{
				 destination_file = fileArray[i].name;
				 var goodName = fileNameFromUrl(destination_file);
				 destination_file = asperaSessionId +"_"+goodName;//destination_file.substring(index);

				 fileNames.push(goodName);
				 transferSpec.paths[i] = {source: fileArray[i].name , destination: destination_file };
			}
			showProgress(locale.resource("AsperaUploadStarting"), ac);
			asperaWeb.startTransfer(transferSpec, connectSettings);
		}}, params.options );
	}

	/**
	 * File is pre-selected, perform upload, handle Events
	 * @param {Deferred} dfd
	 * @param {CNL} documentContainerUrl - CNL of file container
	 * @param {HstDataTransfer} asperaData - information about Aspera connection from selectFile()
	 * @param {Controller} ac - action controller
	 */
	function handleUploadSelected(dfd, documentContainerUrl, asperaData, ac){
		if (!asperaData) {
			dfd.reject(locale.resource("ErrorNoFile"));
			return;
		}
		const {asperaWeb, asperaSessionId, transferSpec, connectSettings, fileNames} = asperaData;
		const onSuccess = () => {
		    updateFileVault(dfd, fileNames, documentContainerUrl, "Files", asperaSessionId, true, true);
		};
		const onTransfer = (event, returnObj) => {
		    handleTransferEvents(dfd, ac, event, returnObj, onTransfer, asperaWeb, OP_UPLOAD, onSuccess);
		}
		asperaWeb.addEventListener(EVENT_TRANSFER, onTransfer);

		showProgress(locale.resource("AsperaUploadStarting"), ac);
		asperaWeb.startTransfer(transferSpec, connectSettings);
	}

// ----- UPLOAD END ----
// ----- INITIALIZATION ----

	/**
	 * Aspear Initialization
	 * @param {Deferred} dfd
	 * @param {Function) callback - function to upload or download handler
	 * @param {Object} responseData - data received from NodeAPIConnect request
	 * @param {CNL} documentContainerUrl - CNL of fileContainer for uploaded file(s) or CNL of CurrentRevision for file replace operation
	 * @param {Object} params
	 */
	function initAspera(dfd, callback, responseData, documentContainerUrl, fileAttr, isDocument, params){
		// Aspera Session ID - a random number used for creating multiple instances of downloads/uploads
		var randomId = new Date().getTime();
		var dfds = [];

		if (typeof AW4 == "undefined"){
			dfds.push(loadScript(URL_ASPERA_WEB));
		}
		all(dfds).then(function(){
			var asperaWeb = new AW4.Connect({
				sdkLocation: ASPERA_LOCATION,
				minVersion: '3.6.0',
				id: "aspera_web_transfers-" + randomId
			});

			var asperaInstaller = new AW4.ConnectInstaller({
				sdkLocation: ASPERA_LOCATION
			});
			var statusEventListener = function (eventType, data) {
				var status = AW4.Connect.STATUS;
				if (eventType === AW4.Connect.EVENT.STATUS) {
					if (data === status.INITIALIZING) {
						asperaInstaller.showLaunching();
					}
					if (data === status.FAILED) {
						asperaInstaller.showDownload();
						dfd.reject(data);
					}
					if (data === status.OUTDATED) {
						asperaInstaller.showUpdate();
					}
					if (data === status.RUNNING) {
						asperaInstaller.connected();
						callback(dfd, randomId, asperaWeb, responseData, documentContainerUrl, fileAttr, isDocument, params);
					}
				}
			};
			asperaWeb.addEventListener(AW4.Connect.EVENT.STATUS, statusEventListener);
			asperaWeb.initSession('nodeConnect-' + randomId);
		});
	}

	/**
	 * Send NodeAPIConnect request to get Aspera spec
	 * Aspera transfer Spec will be extracted from response data in Upload/Download handlers
	 * @param {Deferred} dfd
	 * @param {Function) callback - function to upload or download handler
	 * @param {CNL} documentContainerUrl - CNL of fileContainer for uploaded file(s) or CNL of CurrentRevision for file replace operation
	 * @param {Object} params
	 */
	function init(dfd, callback, documentContainerUrl, fileAttr, isDocument, params){
		var req = new PostRequest("SiteAdmin", "NodeAPIConnect");
		_.each(params, function(value, key){
			req.addParameter(key, value);
		});

		req.post({handleAs: "json"}).then(
				function (response) {
					initAspera(dfd, callback, response.data, documentContainerUrl, fileAttr, isDocument, params);
				},
				function (err) {
					var msg = err.message;
					showError(msg);
					dfd.reject(msg);
				});
	}

// ----- INITIALIZATION END ----
// ----- INTERFACE ----

	return {

		/**
		 * Return true if ASpera is enabled on the server
		 */
		isEnabled: function() {
			return sessionConstants.isAsperaEnabled && !isAsperaValid && crews.isInGlobalRoles(["HST Enabled"]);
		},

		isValid: function() {
			return isAsperaValid;
		},

		/**
		 * Select and upload file
		 * @param {CNL} documentContainerUrl - CNL of fileContainer for uploaded file(s) or CNL of CurrentRevision for file replace operation
		 * @param {boolean} bReplace - replace existing file rather then creating new file
		 * @param {Controller} ac - action controller
		 * @param {Object} options
		 * @return {Deferred}
		 */
		uploadFile: function(documentContainerUrl, fileAttr, bReplace, isDocument, ac, options){
			var dfd = new Deferred();
			init(dfd, handleUpload, documentContainerUrl, fileAttr, isDocument, {Upload:"/", bReplace, ac, options});
			return dfd;
		},

		/**
		 * Download file
		 * @param {string} fileUrl - file CNL
		 * @return {Deferred}
		 */
		downloadFile: function(fileUrl){
			var dfd = new Deferred();
			init(dfd, handleDownload, "", "", true, {"Download":fileUrl});
			return dfd;
		},

		downloadFileFromSpec: function(responseData) {
			var dfd = new Deferred();
			initAspera(dfd, handleDownload, responseData);
			return dfd;
		},

		/**
		 * Init Aspera transfer, show file selection dialog
		 *
		 * IMPORTANT NOTE: Aspera will not work with files selected in external JS file select dialogs
		 * Aspera will not work with File blob structure from external sources, nor will export File blob selected with Aspera
		 *
		 * https://developer.asperasoft.com/web/connect-client/getting-started/29-transfer-specifications-transferspec
		 * Quote: "For security reasons, source paths submitted to the Connect API for upload transfers (AW4.Connect#startTransfer)
		 * must be obtained either using the showSelectFileDialog/showSelectFolderDialogcalls or setDragDropTargets calls.
		 * That's the way to make sure that users are aware of the transfer happening between their host and the remote server."
		 *
		 * @param {Object} options - allowed file extensions
		 * @return {Deferred}
		 */
		selectFile: function(options){
			var dfd = new Deferred();
			init(dfd, handleSelect, "", "", true, {"Upload":"/", options} );
			return dfd;
		},

		/**
		 * Upload files selected in selectFile function. TO be used in conjunction with selectFile
		 * @param {CNL} documentContainerUrl
		 * @param {string} docName
		 * @param {HstDataTransfer} asperaData - Aspera connection details received in selectFile call.
		 * @param {Controller} ac - action controller
		 * @param {boolean} bMaterial - create material rather then document
		 * @param {boolean} bPropagate - Propagate3DMaterials
		 * @return {Deferred}
		 */
		upload3DFile: function(documentContainerUrl, docName, asperaData, ac, bMaterial, bPropagate){
			var dfd = new Deferred();
			handleUpload3D(dfd, documentContainerUrl, docName, asperaData, ac, bMaterial, bPropagate);
			return dfd;
		},

		/**
		 * Upload files selected in selectFile function. TO be used in conjunction with selectFile
		 * @param {CNL} documentContainerUrl
		 * @param {HstDataTransfer} asperaData - Aspera connection details received in selectFile call.
		 * @param {Controller} ac - action controller
		 * @return {Deferred}
		 */
		uploadContractualFile: function(documentContainerUrl, asperaData, ac){
			var dfd = new Deferred();
			handleUploadContractual(dfd, documentContainerUrl, asperaData, ac);
			return dfd;
		},

		/**
		 * Upload files selected in selectFile function. TO be used in conjunction with selectFile
		 * @param {CNL} documentContainerUrl
		 * @param {HstDataTransfer} asperaData - Aspera connection details received in selectFile call.
		 * @param {Controller} ac - action controller
		 * @return {Deferred}
		 */
		uploadSelectedFile: function(documentContainerUrl, asperaData, ac){
			var dfd = new Deferred();
			handleUploadSelected(dfd, documentContainerUrl, asperaData, ac);
			return dfd;
		}
	};
});